import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { CurrencyType, type ShipmentListDTO } from '@uturn/api/v1';
import { GetShipmentsParams } from '@uturn/api/v3';
import { OpenShipments as OpenShipmentsEmptyState } from '@uturn/design-tokens/empty-states';
import {
	RequirementBadges,
	ShipmentPage,
} from '@uturn/portal/modules/shipments/components';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import { formatPrice } from '@uturn/portal/utils';
import { EmptyState } from '@uturn/ui-kit';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { cleanFilterParams } from '../../components/filters/utils-filter-params';
import { ShipmentGroupDetailContext } from '../../providers/shipment-group-detail';

function PublishedShipmentEmptyState() {
	const { t } = useTranslation();
	return (
		<EmptyState>
			<OpenShipmentsEmptyState className="md:h-80" />
			<h2 className="text-secondary-700 text-2xl font-medium leading-7">
				{t(
					'pages.shipments.group-detail.published.emptyState.title',
					'Time to start shipping!',
				)}
			</h2>
			<p className="text-secondary-600 text-lg leading-6">
				{t(
					'pages.shipments.group-detail.published.emptyState.description',
					"We noticed that you don't have any open shipments in your group right now. Publish a shipment from your a concepts first.",
				)}
			</p>
		</EmptyState>
	);
}

export function PublishedGroupShipments() {
	const { t } = useTranslation();
	const { groupName } = useContext(ShipmentGroupDetailContext);
	const { defaultFilters, setShipmentsSearchFilters } =
		useShipmentsSearchStore();

	const router = useRouter();
	const navigate = useNavigate({ from: '/shipments/groups/$groupId/' });
	const search = useSearch({
		from: '/_protected/shipments/groups/$groupId/',
	});

	const shipmentDetails = (shipment: ShipmentListDTO) => [
		{
			label: t('pages.shipments.details.reference'),
			value: shipment.shipperReference,
			visibleOnMultiSelect: true,
			enableCopyToClipboard: true,
		},
		{
			label: t('pages.shipments.details.containerNumber'),
			value: shipment.containerNumber,
			visibleOnMultiSelect: true,
			enableCopyToClipboard: true,
		},
		{
			label: t('pages.shipments.details.totalPrice', 'Total price'),
			value: formatPrice(
				shipment.priceIncludingShipmentFee?.quantity ?? 0,
				shipment.priceIncludingShipmentFee?.currencyCode ?? CurrencyType.EUR,
			),
			visibleOnMultiSelect: true,
			hasInfoIcon: true,
		},
		{
			label: t('pages.shipments.details.requirements'),
			value: <RequirementBadges requirements={shipment.requirements} />,
		},
	];

	return (
		<>
			<Helmet
				title={t(
					'pages.shipments.groups.published.title',
					'{{groupName}} - Published shipments',
					{
						groupName,
					},
				).toString()}
			/>
			<ShipmentPage
				enableGroupFilter={false}
				EmptyState={PublishedShipmentEmptyState}
				details={shipmentDetails}
				hasQuotes
				search={{
					...search,
					page: search.page.toString(),
					size: search.size.toString(),
				}}
				filterOnChange={(filters) => {
					if (filters === null || Object.keys(filters).length === 0) {
						return router.invalidate();
					}

					const newFilters: GetShipmentsParams = {
						...defaultFilters,
						...cleanFilterParams(filters),
					};

					setShipmentsSearchFilters(newFilters);

					navigate({
						search: {
							...newFilters,
							page: Number(newFilters.page),
							size: Number(newFilters.size),
						},
					});
				}}
			/>
		</>
	);
}
